import React, {createContext, useContext, useState, ReactNode, useMemo} from 'react'
import {useLocation} from 'react-router-dom'
import {useAuth} from '../modules/auth'
import ConsumerWidget from '../ConsumerWidget'
import RequesterWidget from '../RequesterWidget'
import {isWidgetFreeRoute} from '../routing/widgetFreeRoutes'

export type CallMode = 'standard' | 'ai-interpretation'

// Pre-call configuration interface
export interface PreCallConfig {
  type: 'audio' | 'video'
  gender: 'male' | 'female' | 'any' | 'non-binary' | 'prefer-not-to-disclose'
  language: string
  transcribe: boolean
  record: boolean
  meetingPin: string
  callMode: CallMode
}

interface ConsumerWidgetContextType {
  // Form state
  callType: 'audio' | 'video'
  setCallType: (type: 'audio' | 'video') => void

  gender: 'male' | 'female' | 'any' | 'non-binary' | 'prefer-not-to-disclose'
  setGender: (gender: 'male' | 'female' | 'any' | 'non-binary' | 'prefer-not-to-disclose') => void

  language: string
  setLanguage: (language: string) => void

  transcribe: boolean
  setTranscribe: (transcribe: boolean) => void

  record: boolean
  setRecord: (record: boolean) => void

  meetingPin: string
  setMeetingPin: (meetingPin: string) => void

  callMode: CallMode
  setCallMode: (callMode: CallMode) => void

  // Helper function to get the complete config
  getPreCallConfig: PreCallConfig

  // Reset form to defaults
  resetForm: () => void

  placeCall: boolean
  setPlaceCall: (placeCall: boolean) => void
}

const ConsumerWidgetContext = createContext<ConsumerWidgetContextType | undefined>(undefined)

interface ConsumerWidgetProviderProps {
  children: ReactNode
}

export const ConsumerWidgetProvider: React.FC<ConsumerWidgetProviderProps> = ({children}) => {
  // Hooks
  const {currentUser, auth} = useAuth()
  const {pathname} = useLocation()
  const hideFloatingWidgets = isWidgetFreeRoute(pathname)

  // Form state
  const [callType, setCallType] = useState<'audio' | 'video'>('audio')
  const [gender, setGender] = useState<"male" | "female" | "any" | "non-binary" | "prefer-not-to-disclose">('any')
  const [language, setLanguage] = useState<string>('')
  const [transcribe, setTranscribe] = useState<boolean>(false)
  const [record, setRecord] = useState<boolean>(false)
  const [meetingPin, setMeetingPin] = useState<string>('')
  const [callMode, setCallMode] = useState<CallMode>('standard')

  const [placeCall, setPlaceCall] = useState<boolean>(false)

  const getPreCallConfig = useMemo<PreCallConfig>(
    () => ({
      type: callType,
      gender,
      language,
      transcribe,
      record,
      meetingPin,
      callMode,
    }),
    [callType, gender, language, transcribe, record, meetingPin, callMode]
  )

  const resetForm = () => {
    setCallType('audio')
    setGender('any')
    setLanguage('')
    setTranscribe(false)
    setRecord(false)
    setMeetingPin('')
    setCallMode('standard')
  }

  const value: ConsumerWidgetContextType = {
    callType,
    setCallType,
    gender,
    setGender,
    language,
    setLanguage,
    transcribe,
    setTranscribe,
    record,
    setRecord,
    meetingPin,
    setMeetingPin,
    callMode,
    setCallMode,
    getPreCallConfig,
    resetForm,
    placeCall,
    setPlaceCall,
  }

  return (
    <ConsumerWidgetContext.Provider value={value}>
      {children}

      {!hideFloatingWidgets && <ConsumerWidget />}

      {!hideFloatingWidgets && <RequesterWidget />}
    </ConsumerWidgetContext.Provider>
  )
}

export const useConsumerWidget = (): ConsumerWidgetContextType => {
  const context = useContext(ConsumerWidgetContext)
  if (context === undefined) {
    throw new Error('useConsumerWidget must be used within a ConsumerWidgetProvider')
  }
  return context
}
